import React from "react";

// UI components
import { Col, Row } from "react-bootstrap";
import {
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/AddOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import ArrowUpIcon from "@mui/icons-material/KeyboardArrowUpOutlined";
import ArrowDownIcon from "@mui/icons-material/KeyboardArrowDownOutlined";

const newMilestoneId = () =>
  `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * Seller-side editor for the shared plan. Milestones are plain objects:
 * {id, title, description, due (unix ts or 0), assignee: seller|buyer,
 *  status: pending|done}. Buyers can tick off buyer-assigned items from
 * the portal.
 */
export default function MilestoneBuilder(props) {
  const { milestones, setMilestones } = props;

  const update = (id, changes) => {
    setMilestones(
      milestones.map((milestone) =>
        milestone.id === id ? { ...milestone, ...changes } : milestone
      )
    );
  };

  const move = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= milestones.length) {
      return;
    }
    const reordered = [...milestones];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setMilestones(reordered);
  };

  return (
    <div className="milestone-builder">
      {milestones.length === 0 && (
        <Typography owner="admin" variant="body2" className="mb-2">
          No milestones yet. Build a shared plan the buyer can follow —
          e.g. “Demo call”, “Proposal review”, “Contract signed”.
        </Typography>
      )}

      {milestones.map((milestone, index) => (
        <Row
          className="milestone-builder__row align-items-center gx-2 mb-2"
          key={milestone.id}
        >
          <Col xs={12} md={3}>
            <TextField
              className="w-100"
              variant="outlined"
              size="small"
              value={milestone.title}
              sx={{ "& .MuiInputBase-input": { height: 28 } }}
              placeholder="Milestone"
              onInput={(e) => update(milestone.id, { title: e.target.value })}
            />
          </Col>
          <Col xs={12} md={3}>
            <TextField
              className="w-100"
              variant="outlined"
              size="small"
              value={milestone.description}
              sx={{ "& .MuiInputBase-input": { height: 28 } }}
              placeholder="Details (optional)"
              onInput={(e) =>
                update(milestone.id, { description: e.target.value })
              }
            />
          </Col>
          <Col xs={6} md={2}>
            <TextField
              className="w-100 date-input-field"
              type="date"
              variant="outlined"
              size="small"
              value={milestone.dueInput || ""}
              onChange={(e) => update(milestone.id, { dueInput: e.target.value })}
            />
          </Col>
          <Col xs={6} md={2}>
            <FormControl className="w-100" size="small">
              <InputLabel id={`assignee-${milestone.id}`}>Assignee</InputLabel>
              <Select
                labelId={`assignee-${milestone.id}`}
                value={milestone.assignee}
                onChange={(e) =>
                  update(milestone.id, { assignee: e.target.value })
                }
                input={<OutlinedInput label="Assignee" />}
              >
                <MenuItem value="seller">Us</MenuItem>
                <MenuItem value="buyer">Buyer</MenuItem>
              </Select>
            </FormControl>
          </Col>
          <Col xs={6} md={1}>
            <FormControl className="w-100" size="small">
              <InputLabel id={`status-${milestone.id}`}>Status</InputLabel>
              <Select
                labelId={`status-${milestone.id}`}
                value={milestone.status}
                onChange={(e) => update(milestone.id, { status: e.target.value })}
                input={<OutlinedInput label="Status" />}
              >
                <MenuItem value="pending">Open</MenuItem>
                <MenuItem value="done">Done</MenuItem>
              </Select>
            </FormControl>
          </Col>
          <Col xs={6} md={1} className="d-flex justify-content-end">
            <IconButton
              size="small"
              disabled={index === 0}
              onClick={() => move(index, -1)}
            >
              <ArrowUpIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              disabled={index === milestones.length - 1}
              onClick={() => move(index, 1)}
            >
              <ArrowDownIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() =>
                setMilestones(milestones.filter((m) => m.id !== milestone.id))
              }
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Col>
        </Row>
      ))}

      <Button
        variant="contained"
        color="secondary"
        className="action"
        size="small"
        sx={{ textTransform: "none" }}
        startIcon={<AddIcon />}
        onClick={() =>
          setMilestones([
            ...milestones,
            {
              id: newMilestoneId(),
              title: "",
              description: "",
              dueInput: "",
              assignee: "seller",
              status: "pending",
            },
          ])
        }
      >
        Add milestone
      </Button>
    </div>
  );
}
